import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import Grid from "@mui/material/Grid";
import * as Yup from "yup";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Box, Breadcrumbs } from "@mui/material";
import LayersIcon from "@mui/icons-material/Layers";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "../../../components/TextField";
import GridTable from "../../../components/GridTable";
import ActionMenu from "../../../components/ActionMenu";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import { successMessage, errorMessage } from "../../../components/Toaster";
import {
    getVersionsByMachineId,
    createMachineVersion,
    updateMachineVersion,
    deleteMachineVersion,
} from "./api";
import {
    ApiResponse,
    MachineVersionData,
    CreateMachineVersionParams,
    UpdateMachineVersionParams,
} from "./interface";
import { getErrorMessage } from "../../../utils/error";

const Version: React.FC = () => {
    const location = useLocation();
    const { machine_id } = useParams();
    const navigate = useNavigate();
    const machine_name = location.state?.machine_name || "Version";
    const [versions, setVersions] = useState<MachineVersionData[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [editVersion, setEditVersion] = useState<MachineVersionData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        update: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        version: Yup.string().required("Version is required"),
        description: Yup.string().nullable(),
        coinRadius: Yup.number()
            .typeError("Coin radius must be a number")
            .required("Coin radius is required"),
        coinRadiusBottom: Yup.number()
            .typeError("Coin radius must be a number")
            .nullable(),
    });

    const columns: GridColDef[] = [
        { field: "version", headerName: "Version", flex: 1 },
        { field: "description", headerName: "Description", flex: 1.5 },
        { field: "coin_radius", headerName: "Coin Radius", flex: 1 },
        { field: "coin_radius_bottom", headerName: "Coin Radius Bottom", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: () => handleEdit(params),
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                        {
                            icon: <PrecisionManufacturingIcon color="success" />,
                            tooltip: "Instance",
                            onClick: handleViewInstance,
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        const fetchVersions = async () => {
            if (!machine_id) return;
            setLoading(prev => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<MachineVersionData[]> = await getVersionsByMachineId(machine_id);
                if (res.status && res.data) {
                    setVersions(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch versions.");
                }
            } catch (error) {
                errorMessage(getErrorMessage(error) || "Error fetching versions.");
            } finally {
                setLoading(prev => ({ ...prev, fetch: false }));
            }
        };

        fetchVersions();
    }, [machine_id]);

    const openDialog = () => setDialogOpen(true);
    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditVersion(null);
        setDialogOpen(false);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditVersion(params.row as MachineVersionData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const handleViewInstance = (params: GridCellParams) => {
        const machine_version_id = params.row.machine_version_id;
        const version = params.row.version;
        navigate(`/machines/${machine_id}/versions/${machine_version_id}/instances`, {
            state: { machine_name, version }
        });
    };

    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        let success = false;
        if (editVersion) {
            success = await handleUpdate(values);
        } else {
            success = await handleCreate(values);
        }
        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: any) => {
        setLoading(prev => ({ ...prev, create: true }));
        try {
            const newVersionParams: CreateMachineVersionParams = {
                machine_id: machine_id!,
                version: values.version.trim(),
                description: values.description?.trim() || null,
                coin_radius: Number(values.coinRadius),
                coin_radius_bottom: values.coinRadiusBottom ? Number(values.coinRadiusBottom) : null,
            };

            const response = await createMachineVersion(newVersionParams);
            if (response.status) {
                const newVersion: MachineVersionData = {
                    machine_version_id: response.data!.machine_version_id,
                    machine_id: machine_id!,
                    version: values.version,
                    description: values.description,
                    coin_radius: Number(values.coinRadius),
                    coin_radius_bottom: values.coinRadiusBottom ? Number(values.coinRadiusBottom) : null,
                };
                setVersions(prev => [...prev, newVersion]);
                successMessage(response.message);
                return true;
            } else {
                errorMessage(response.message || "Failed to create version.");
                return false;
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
            return false;
        } finally {
            setLoading(prev => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: any) => {
        setLoading(prev => ({ ...prev, update: true }));
        try {
            const updatedData: UpdateMachineVersionParams = {
                machine_version_id: editVersion?.machine_version_id || "",
                version: values.version.trim(),
                description: values.description?.trim() || null,
                coin_radius: Number(values.coinRadius),
                coin_radius_bottom: values.coinRadiusBottom ? Number(values.coinRadiusBottom) : null,
            };
            const response = await updateMachineVersion(updatedData);
            if (response.status) {
                setVersions(prev =>
                    prev.map(version =>
                        version.machine_version_id === editVersion?.machine_version_id
                            ? { ...version, ...updatedData }
                            : version
                    )
                );
                successMessage(response.message || "Version updated successfully!");
                return true;
            } else {
                errorMessage(response.message || "Failed to update version.");
                return false;
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
            return false;
        } finally {
            setLoading(prev => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading(prev => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const response = await deleteMachineVersion(itemToDelete.row.machine_version_id);
                if (response.status) {
                    setVersions(prev => prev.filter(v => v.machine_version_id !== itemToDelete.row.machine_version_id));
                    successMessage(response.message);
                } else {
                    errorMessage(response.message || "Failed to delete version.");
                }
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || "Unexpected error occurred.");
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <div
                            style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}
                        >
                            <Breadcrumbs
                                separator={<NavigateNextIcon fontSize="small" />}
                                aria-label="breadcrumb"
                                style={{ fontSize: "18px", fontWeight: "bold" }}
                            >
                                <Link color="primary" to="/machines">
                                    Machines
                                </Link>
                                <Link color="primary" to={`/machines`}>{machine_name}</Link>
                                <Link color="primary" to={`/machines/${machine_id}/versions`} state={{ machine_name: machine_name }}>Versions</Link>
                            </Breadcrumbs>
                        </div>
                        <div className="d-flex justify-end">
                            <div className="d-flex justify-between w-100">
                                <TextField
                                    className="w-25"
                                    label="Search"
                                    variant="outlined"
                                    value={searchQuery}
                                    size="small"
                                    startIcon={<SearchIcon />}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                />
                            </div>
                        </div>
                        <div className="ag-theme-alpine m-t-20" style={{ height: "100%", width: "100%" }}>
                            <GridTable
                                rows={versions}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.machine_version_id}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                version: editVersion?.version || "",
                                description: editVersion?.description || "",
                                coinRadius: editVersion?.coin_radius ?? "",
                                coinRadiusBottom: editVersion?.coin_radius_bottom ?? "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({ values, errors, touched, handleChange, handleSubmit, resetForm }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={editVersion ? "Edit Version" : "Create Version"}
                                    confirmButtonText={editVersion ? "Update" : "Add"}
                                    cancelButtonText="Cancel"
                                    icon={<LayersIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.create || loading.update ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={4}>
                                                            <TextField
                                                                label="Version"
                                                                name="version"
                                                                value={values.version}
                                                                onChange={handleChange}
                                                                error={Boolean(errors.version && touched.version)}
                                                                helperText={touched.version && errors.version}
                                                                required
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                label="Coin Radius"
                                                                name="coinRadius"
                                                                type="number"
                                                                value={values.coinRadius}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    if (val === "" || Number(val) >= 0) {
                                                                        handleChange(e);
                                                                    }
                                                                }}
                                                                inputProps={{ min: 0, step: "0.01" }}
                                                                error={Boolean(errors.coinRadius && touched.coinRadius)}
                                                                helperText={touched.coinRadius && errors.coinRadius}
                                                                required
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                label="Coin Radius Bottom"
                                                                name="coinRadiusBottom"
                                                                type="number"
                                                                value={values.coinRadiusBottom}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    if (val === "" || Number(val) >= 0) {
                                                                        handleChange(e);
                                                                    }
                                                                }}
                                                                inputProps={{ min: 0, step: "0.01" }}
                                                                error={Boolean(errors.coinRadiusBottom && touched.coinRadiusBottom)}
                                                                helperText={touched.coinRadiusBottom && errors.coinRadiusBottom}
                                                            />
                                                        </Grid>
                                                        <Grid size={4}>
                                                            <TextField
                                                                label="Description"
                                                                name="description"
                                                                value={values.description}
                                                                onChange={handleChange}
                                                                error={Boolean(errors.description && touched.description)}
                                                                helperText={touched.description && errors.description}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                    </main>
                </Box>
            )}
            <Dialog
                open={deleteOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={!loading.delete ? "Delete Version" : ""}
                confirmButtonText={!loading.delete ? "Confirm" : ""}
                cancelButtonText={!loading.delete ? "Cancel" : ""}
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to delete this version?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Version;